import React from 'react';
import {
    Button,
    Checkbox,
    Dropdown,
    Field,
    Input,
    Option,
    Text,
    Textarea,
    makeStyles,
    shorthands,
} from '@fluentui/react-components';
import { Add24Regular, Delete24Regular } from '@fluentui/react-icons';
import { Controller, useFieldArray } from 'react-hook-form';
import { customColors, customFonts } from '../../../../styles';
import { functionToolArrayItemTypes, functionToolParameterTypes } from '../../../../domain/agents/models/FunctionTool';
import { createDefaultFunctionToolFormParameter, createDefaultFunctionToolFormDefinition } from './FunctionToolForm';

const useStyles = makeStyles({
    toolCard: {
        display: 'flex',
        flexDirection: 'column',
        gap: '12px',
        ...shorthands.padding('16px'),
        ...shorthands.borderRadius('10px'),
        ...shorthands.border('1px', 'solid', customColors.lightGray),
        backgroundColor: '#fafafa',
    },
    cardHeader: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        paddingBottom: '12px',
        ...shorthands.borderBottom('1px', 'solid', customColors.lightGray),
    },
    functionLabel: {
        fontFamily: customFonts.BarlowCondensed,
        color: customColors.summarizeButtonDarker,
        fontSize: '16px',
        fontWeight: '600',
        letterSpacing: '0.01em',
    },
    parameterContainer: {
        display: 'flex',
        flexDirection: 'column',
        gap: '12px',
        ...shorthands.padding('12px'),
        ...shorthands.borderRadius('8px'),
        backgroundColor: '#efefef',
    },
    parameterHeader: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: '12px',
    },
    parameterMetaRow: {
        display: 'flex',
        alignItems: 'center',
        gap: '12px',
        flexWrap: 'wrap',
    },
    parameterFieldsRow: {
        display: 'grid',
        gridTemplateColumns: '1fr 1fr',
        gap: '12px',
        '@media (max-width: 640px)': {
            gridTemplateColumns: 'minmax(0, 1fr)',
        },
    },
    subSection: {
        display: 'flex',
        flexDirection: 'column',
        gap: '12px',
        ...shorthands.padding('12px'),
        ...shorthands.borderRadius('8px'),
    },
    sectionTitle: {
        fontFamily: customFonts.Barlow,
        color: customColors.summarizeButtonDarker,
        fontSize: '14px',
        fontWeight: '600',
    },
    hintText: {
        fontFamily: customFonts.Barlow,
        color: customColors.headerLightGray,
        fontSize: '13px',
    },
    actionRow: {
        display: 'flex',
        gap: '10px',
        flexWrap: 'wrap',
    },
    compactButton: {
        width: 'fit-content',
        ...shorthands.borderRadius('8px'),
    },
});

interface FunctionToolParametersFieldArrayProps {
    control: any;
    name: string;
    watch: any;
    setValue: any;
    title: string;
    addLabel: string;
}

const FunctionToolParametersFieldArray: React.FC<FunctionToolParametersFieldArrayProps> = ({
    control,
    name,
    watch,
    setValue,
    title,
    addLabel,
}) => {
    const styles = useStyles();
    const { fields, append, remove } = useFieldArray({ control, name });
    const parameters = watch(name, fields);

    const resetNestedParameterState = (index: number, nextType?: string): void => {
        if (nextType !== 'object') {
            setValue(`${name}.${index}.properties`, [], { shouldDirty: true });
        }

        if (nextType !== 'array') {
            setValue(`${name}.${index}.itemsType`, '', { shouldDirty: true });
            setValue(`${name}.${index}.items`, [], { shouldDirty: true });
        }

        if (nextType !== 'string') {
            setValue(`${name}.${index}.enumValues`, '', { shouldDirty: true });
        }
    };

    return (
        <div className={styles.subSection}>
            <Text className={styles.sectionTitle}>{title}</Text>
            {fields.length === 0 && <Text className={styles.hintText}>No entries added yet.</Text>}

            {fields.map((field, index) => {
                const parameter = parameters?.[index];
                const type = parameter?.type;
                const itemType = parameter?.itemsType;

                return (
                    <div key={field.id} className={styles.parameterContainer}>
                        <div className={styles.parameterHeader}>
                            <Text className={styles.sectionTitle}>Parameter {index + 1}</Text>
                            <Button
                                type="button"
                                appearance="subtle"
                                icon={<Delete24Regular />}
                                onClick={() => remove(index)}
                            />
                        </div>

                        <div className={styles.parameterFieldsRow}>
                            <Controller
                                control={control}
                                name={`${name}.${index}.name`}
                                render={({ field: inputField }) => (
                                    <Field label="Name">
                                        <Input {...inputField} placeholder="Parameter name" />
                                    </Field>
                                )}
                            />

                            <Controller
                                control={control}
                                name={`${name}.${index}.type`}
                                render={({ field: dropdownField }) => (
                                    <Field label="Type">
                                        <Dropdown
                                            placeholder="Select a type"
                                            selectedOptions={dropdownField.value ? [dropdownField.value] : []}
                                            value={dropdownField.value}
                                            onOptionSelect={(_event, data) => {
                                                resetNestedParameterState(index, data.optionValue);
                                                dropdownField.onChange(data.optionValue ?? '');
                                            }}
                                        >
                                            {functionToolParameterTypes.map((option) => (
                                                <Option key={option} value={option}>
                                                    {option}
                                                </Option>
                                            ))}
                                        </Dropdown>
                                    </Field>
                                )}
                            />
                        </div>

                        <Controller
                            control={control}
                            name={`${name}.${index}.description`}
                            render={({ field: inputField }) => (
                                <Field label="Description">
                                    <Textarea {...inputField} placeholder="Describe what this parameter does" />
                                </Field>
                            )}
                        />

                        <div className={styles.parameterMetaRow}>
                            <Controller
                                control={control}
                                name={`${name}.${index}.required`}
                                render={({ field: checkboxField }) => (
                                    <Checkbox
                                        checked={checkboxField.value}
                                        label="Required"
                                        onChange={(_event, data) => checkboxField.onChange(data.checked)}
                                    />
                                )}
                            />
                        </div>

                        {type === 'string' && (
                            <Controller
                                control={control}
                                name={`${name}.${index}.enumValues`}
                                render={({ field: inputField }) => (
                                    <Field label="Enum Values">
                                        <Input {...inputField} placeholder="Comma-separated values, optional" />
                                    </Field>
                                )}
                            />
                        )}

                        {type === 'object' && (
                            <FunctionToolParametersFieldArray
                                control={control}
                                name={`${name}.${index}.properties`}
                                watch={watch}
                                setValue={setValue}
                                title="Object Properties"
                                addLabel="Add Property"
                            />
                        )}

                        {type === 'array' && (
                            <>
                                <Controller
                                    control={control}
                                    name={`${name}.${index}.itemsType`}
                                    render={({ field: dropdownField }) => (
                                        <Field label="Array Item Type">
                                            <Dropdown
                                                placeholder="Select an item type"
                                                selectedOptions={dropdownField.value ? [dropdownField.value] : []}
                                                value={dropdownField.value}
                                                onOptionSelect={(_event, data) => {
                                                    if (data.optionValue !== 'object') {
                                                        setValue(`${name}.${index}.items`, [], { shouldDirty: true });
                                                    }

                                                    dropdownField.onChange(data.optionValue ?? '');
                                                }}
                                            >
                                                {functionToolArrayItemTypes.map((option) => (
                                                    <Option key={option} value={option}>
                                                        {option}
                                                    </Option>
                                                ))}
                                            </Dropdown>
                                        </Field>
                                    )}
                                />

                                {itemType === 'object' && (
                                    <FunctionToolParametersFieldArray
                                        control={control}
                                        name={`${name}.${index}.items`}
                                        watch={watch}
                                        setValue={setValue}
                                        title="Array Item Properties"
                                        addLabel="Add Item Property"
                                    />
                                )}
                            </>
                        )}
                    </div>
                );
            })}

            <Button
                type="button"
                appearance="secondary"
                className={styles.compactButton}
                icon={<Add24Regular />}
                onClick={() => append(createDefaultFunctionToolFormParameter())}
            >
                {addLabel}
            </Button>
        </div>
    );
};

interface FunctionToolsFieldArrayProps {
    control: any;
    name: string;
    watch: any;
    setValue: any;
}

const FunctionToolsFieldArray: React.FC<FunctionToolsFieldArrayProps> = ({ control, name, watch, setValue }) => {
    const styles = useStyles();
    const { fields, append, remove } = useFieldArray({ control, name });

    return (
        <div className={styles.actionRow}>
            <div style={{ width: '100%', display: 'flex', flexDirection: 'column', gap: '12px' }}>
                {fields.length === 0 && (
                    <Text className={styles.hintText}>
                        Add one or more function tools to let the agent call custom functions.
                    </Text>
                )}

                {fields.map((field, index) => (
                    <div key={field.id} className={styles.toolCard}>
                        <div className={styles.cardHeader}>
                            <Text className={styles.functionLabel}>Function {index + 1}</Text>
                            <Button
                                type="button"
                                appearance="subtle"
                                className={styles.compactButton}
                                icon={<Delete24Regular />}
                                onClick={() => remove(index)}
                            />
                        </div>

                        <Controller
                            control={control}
                            name={`${name}.${index}.name`}
                            render={({ field: inputField }) => (
                                <Field label="Function Name">
                                    <Input {...inputField} placeholder="Function name" />
                                </Field>
                            )}
                        />

                        <Controller
                            control={control}
                            name={`${name}.${index}.description`}
                            render={({ field: inputField }) => (
                                <Field label="Description">
                                    <Textarea {...inputField} placeholder="Explain what this function does" />
                                </Field>
                            )}
                        />

                        <Controller
                            control={control}
                            name={`${name}.${index}.strictMode`}
                            render={({ field: checkboxField }) => (
                                <Checkbox
                                    checked={checkboxField.value}
                                    label="Enable strict parameter validation"
                                    onChange={(_event, data) => checkboxField.onChange(data.checked)}
                                />
                            )}
                        />

                        <FunctionToolParametersFieldArray
                            control={control}
                            name={`${name}.${index}.parameters`}
                            watch={watch}
                            setValue={setValue}
                            title="Parameters"
                            addLabel="Add Parameter"
                        />
                    </div>
                ))}
            </div>

            <Button
                type="button"
                appearance="primary"
                className={styles.compactButton}
                icon={<Add24Regular />}
                onClick={() => append(createDefaultFunctionToolFormDefinition())}
            >
                Add Function Tool
            </Button>
        </div>
    );
};

export default FunctionToolsFieldArray;
